// CommunityTab — the 4th bottom-nav tab (redesign Phase 5).
//
// Extracts and restyles the three previously-inline sub-tabs (Community
// Board, Prayer, Encouragements) into one screen with a mixed "All" feed,
// pinned church notices, and a single composer, per docs/designs/Community.dc.html.
//
// Architecture note: `prayers` and `encouragements` are passed in as PROPS,
// not fetched here. BibleApp.jsx's own usePrayers()/useEncouragements() calls
// also feed the Bible reader's verse-dot cross-referencing (the augmented
// `verseKeys`/`chapterKeys` on each encouragement are read elsewhere in that
// file) — calling those hooks again here would fork that shared state into a
// second, independently-fetched copy. `useCommunityBoard` and `useNotices`
// have no such cross-cutting use, so this component calls those itself.
//
// Props:
//   activeChurchId, churchName, userId, isModerator
//   (no isVisitor needed — the whole tab is already nav-level gated for
//   visitors in BibleApp.jsx, confirmed by earlier research this phase)
//   prayers, addPrayer(text, visibility), togglePray(id, currentlyPrayed),
//     addPrayerComment(prayerId, text), likePrayerComment(commentId, liked),
//     removePrayer(id, text)
//   encouragements, toggleEncouragementLike(id, liked), removeEncouragement(id, text),
//     addEncComment(id, text, parentId?), likeEncComment(id, liked), removeEncComment(id),
//     refreshEncouragements()
//   onOpenVerse(book, chapter, verse) — navigate the Bible reader to a verse
//   onReport(type, id) — open the shared report modal
//   chip, setChip — controlled active chip ("all"|"prayer"|"encourage"|"board"),
//     lifted so push-notification routing can land on a specific one
//   deepLinkHighlight — { type: 'prayer'|'post', id } | null, highlights + scrolls
//     to a specific item when deep-linked
//   onViewPrayerList() — "View" on the "Added to your prayer list" toast,
//     jumps to This Week's PrayerListSheet (useSavedPrayerIds/toggleSavedPrayer
//     are called directly here, per the architecture note above — no other
//     cross-cutting use, same as useCommunityBoard/useNotices)

import { useEffect, useMemo, useState } from "react";
import { ThumbsUp, MessageCircle, Shield } from "lucide-react";
import { supabase } from "../supabase";
import { dedupeRefRows } from "../lib/dedupeRefs";
import { useCommunityBoard, useSavedPrayerIds } from "../hooks/useData";
import { useAuth } from "../hooks/useAuth";
import { useNotices } from "../hooks/useNotices";
import { ScopeToggle } from "./ExploreTab";
import EncouragementUpload from "./EncouragementUpload";
import FaceStack from "./FaceStack";

const GOLD = "var(--gold)";
const SAGE = "var(--sage)";
const SAGE_INK = "var(--sage-ink)";
const SERIF = "'Source Serif 4', Georgia, serif";
const SANS = "'Plus Jakarta Sans', system-ui, sans-serif";

function getInitials(name) {
  return (name || "").split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join("") || "·";
}
function Avatar({ url, name, size = 28 }) {
  const sz = { width: size, height: size, borderRadius: "50%", flexShrink: 0 };
  if (url) return <img src={url} alt="" loading="lazy" style={{ ...sz, objectFit: "cover", display: "block" }}/>;
  return (
    <div style={{ ...sz, background: "rgba(232,200,122,0.14)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: Math.max(10, Math.round(size * 0.36)), fontWeight: 800, color: "var(--gold-ink)" }}>
      {getInitials(name)}
    </div>
  );
}
function LikeButton({ liked, count, onClick, size = 16 }) {
  return (
    <button type="button" onClick={e => { e.stopPropagation(); onClick?.(); }} aria-label={liked ? "Remove like" : "Like"}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "transparent", border: "none", padding: 4, cursor: "pointer", color: liked ? "var(--gold-ink)" : "var(--stone)", fontFamily: SANS, fontSize: 13, fontWeight: 600 }}>
      <ThumbsUp size={size} strokeWidth={1.8} fill={liked ? "currentColor" : "none"}/>
      {count > 0 && <span>{count}</span>}
    </button>
  );
}
function CommentButton({ count, onClick, label, size = 16 }) {
  return (
    <button type="button" onClick={e => { e.stopPropagation(); onClick?.(); }} aria-label={label || "Comments"}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "transparent", border: "none", padding: 4, cursor: "pointer", color: "var(--stone)", fontFamily: SANS, fontSize: 13, fontWeight: 600 }}>
      <MessageCircle size={size} strokeWidth={1.8}/>
      {count > 0 ? <span>{count}</span> : (label ? <span>{label}</span> : null)}
    </button>
  );
}
function ContentActionsMenu({ canDelete, isOwn, onDelete, onReport, extraItems = [] }) {
  const [open, setOpen] = useState(false);
  const showReport = !isOwn && !!onReport;
  const items = [
    ...extraItems,
    ...(showReport ? [{ label: "Report", color: "var(--ink)", onClick: onReport }] : []),
    ...(canDelete ? [{ label: "Remove", color: "#C8372A", onClick: onDelete }] : []),
  ];
  if (items.length === 0) return null;
  return (
    <div style={{ position: "relative", display: "inline-flex" }}>
      <button type="button" aria-label="More options" onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        style={{ background: "none", border: "none", padding: "4px 6px", cursor: "pointer", color: "var(--stone-dim)", display: "inline-flex", alignItems: "center", fontSize: 18, lineHeight: 1 }}>
        ⋮
      </button>
      {open && (
        <>
          <div onClick={e => { e.stopPropagation(); setOpen(false); }} style={{ position: "fixed", inset: 0, zIndex: 90 }}/>
          <div style={{ position: "absolute", top: "100%", right: 0, marginTop: 6, zIndex: 91, background: "var(--white)", border: "1px solid var(--border-strong)", borderRadius: 10, minWidth: 180, overflow: "hidden", boxShadow: "0 10px 24px rgba(0,0,0,0.2)" }}>
            {items.map((it, i) => (
              <div key={it.label} onClick={e => { e.stopPropagation(); setOpen(false); it.onClick(); }}
                style={{ padding: "11px 14px", fontSize: 13, fontWeight: 600, color: it.color, cursor: "pointer", borderTop: i > 0 ? "1px solid var(--border)" : "none" }}>
                {it.label}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
function ConfirmDeleteSheet({ title, subtitle, onCancel, onConfirm, busy }) {
  return (
    <>
      <div style={S.backdrop} onClick={busy ? undefined : onCancel}/>
      <div style={{ ...S.sheet, maxHeight: "60vh" }}>
        <div style={S.grab}/>
        <div style={{ padding: "4px 20px 20px" }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)", lineHeight: 1.4, marginBottom: 8 }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: "var(--stone)", lineHeight: 1.5, marginBottom: 18 }}>{subtitle}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" onClick={busy ? undefined : onCancel}
              style={{ flex: 1, background: "transparent", color: "var(--stone)", border: "1px solid var(--border-strong)", borderRadius: 10, padding: 12, fontSize: 14, fontWeight: 600, cursor: busy ? "default" : "pointer", opacity: busy ? 0.5 : 1, fontFamily: SANS }}>
              Cancel
            </button>
            <button type="button" onClick={busy ? undefined : onConfirm} disabled={busy}
              style={{ flex: 1, background: "#F97066", color: "#fff", border: "none", borderRadius: 10, padding: 12, fontSize: 14, fontWeight: 800, cursor: busy ? "default" : "pointer", opacity: busy ? 0.5 : 1, fontFamily: SANS }}>
              {busy ? "Deleting…" : "Delete"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
function ComposerTypeSheet({ isModerator, onPick, onCancel }) {
  const options = [
    { id: "prayer", label: "Prayer request" },
    ...(isModerator ? [{ id: "encourage", label: "Encouragement" }] : []),
    { id: "board", label: "Board post" },
  ];
  return (
    <>
      <div style={S.backdrop} onClick={onCancel}/>
      <div style={{ ...S.sheet, maxHeight: "50vh" }}>
        <div style={S.grab}/>
        <div style={{ padding: "4px 20px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)", marginBottom: 6 }}>Share as…</div>
          {options.map(o => (
            <button key={o.id} type="button" onClick={() => onPick(o.id)}
              style={{ textAlign: "left", background: "var(--white)", border: "1px solid var(--border)", borderRadius: 12, padding: "12px 14px", fontSize: 14, fontWeight: 600, color: "var(--ink)", cursor: "pointer", fontFamily: SANS }}>
              {o.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
function EncouragementSettingsSheet({ onClose, churchId, onChanged, editing }) {
  const [mode, setMode] = useState(editing ? "edit" : "list");
  const [editTarget] = useState(editing || null);
  return (
    <>
      <div style={S.backdrop} onClick={onClose}/>
      <div style={{ ...S.sheet, maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={S.grab}/>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 16px 0", flexShrink: 0 }}>
          <div style={{ fontFamily: SERIF, fontSize: 17, color: "var(--ink)" }}>{mode === "add" ? "Add an encouragement" : mode === "edit" ? "Edit encouragement" : "Encouragement settings"}</div>
          <button onClick={onClose} style={S.closeBtn}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: "auto" }}>
          {mode === "list" && (
            <div style={{ padding: "8px 16px 16px" }}>
              {!churchId && (
                <div style={{ background: "rgba(249,112,102,0.1)", border: "1px solid rgba(249,112,102,0.25)", borderRadius: 10, padding: "12px 14px", fontSize: 13, color: "#C8372A", marginBottom: 12 }}>
                  Pick an active church in your profile before adding an encouragement.
                </div>
              )}
              <button onClick={() => setMode("add")} disabled={!churchId}
                style={{ width: "100%", background: churchId ? GOLD : "var(--white)", color: churchId ? "var(--ink)" : "var(--stone-dim)", border: "none", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 800, cursor: churchId ? "pointer" : "default", fontFamily: SANS }}>
                + Add encouragement
              </button>
            </div>
          )}
          {mode === "add" && (
            <>
              <div style={{ padding: "8px 16px 0" }}>
                <button onClick={() => setMode("list")} style={{ background: "none", border: "none", color: "var(--stone-dim)", fontSize: 13, cursor: "pointer", padding: "0 0 8px 0", display: "block" }}>← Back</button>
              </div>
              <EncouragementUpload churchId={churchId} onSaved={() => { onChanged?.(); setMode("list"); onClose?.(); }}/>
            </>
          )}
          {mode === "edit" && editTarget && (
            <EncouragementUpload churchId={churchId} existing={editTarget} onSaved={() => { onChanged?.(); onClose?.(); }}/>
          )}
        </div>
      </div>
    </>
  );
}

function fmtRelative(iso) {
  if (!iso) return "";
  const diffMs = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diffMs / 3600000);
  if (h < 1) return "just now";
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function CommunityTab({
  activeChurchId, churchName, userId, isModerator,
  prayers, addPrayer: addPrayerRaw, togglePray: togglePrayRaw, addPrayerComment: addPrayerCommentRaw,
  likePrayerComment: likePrayerCommentRaw, removePrayer,
  encouragements, toggleEncouragementLike, removeEncouragement, addEncComment, likeEncComment, removeEncComment, refreshEncouragements,
  onOpenVerse, onReport, chip, setChip, deepLinkHighlight, onOpenFindChurches, onViewPrayerList,
}) {
  const { posts, addPost, addComment: addBoardComment, likeComment: likeBoardComment, removePost, toggleLike: toggleBoardLike, refresh: refreshBoard } = useCommunityBoard(activeChurchId);
  const { notices } = useNotices(activeChurchId);
  const { savedIds: savedPrayerIds, toggleSaved: toggleSavedPrayer } = useSavedPrayerIds();
  const { profile } = useAuth();
  const [prayerListToast, setPrayerListToast] = useState(false);

  function handleTogglePrayerList(prayerId, currentlySaved) {
    toggleSavedPrayer(prayerId, currentlySaved);
    if (!currentlySaved) {
      setPrayerListToast(true);
      setTimeout(() => setPrayerListToast(false), 3000);
    }
  }

  const canDelete = authorId => isModerator || (!!userId && authorId === userId);

  // ── Composer ──────────────────────────────────────────────────────────
  // On a specific chip, the type is implied by the chip. On "All", the type
  // is chosen via ComposerTypeSheet first (pendingComposerType), then the
  // normal text box appears — reset back to null after each post so the
  // picker is asked again next time.
  const [pendingComposerType, setPendingComposerType] = useState(null);
  const [composerSheetOpen, setComposerSheetOpen] = useState(false);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [postError, setPostError] = useState("");
  const [prayerVisibility, setPrayerVisibility] = useState("church");
  const [prayerAudienceOpen, setPrayerAudienceOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  function toggleExpanded(key) {
    setExpandedIds(prev => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  }

  const effectiveType = chip === "prayer" ? "prayer" : chip === "board" ? "board" : chip === "encourage" ? "encourage" : pendingComposerType;

  async function submitComposer() {
    if (!draft.trim() || posting || !effectiveType) return;
    setPosting(true); setPostError("");
    if (effectiveType === "prayer") {
      await addPrayerRaw(draft.trim(), prayerVisibility);
    } else if (effectiveType === "encourage") {
      const { error } = await supabase.from("encouragements").insert({ church_id: activeChurchId, author_id: userId, text: draft.trim() });
      if (error) { setPostError(error.message); setPosting(false); return; }
      refreshEncouragements?.();
    } else {
      const { error } = await addPost(draft.trim());
      if (error) { setPostError(error); setPosting(false); return; }
    }
    setDraft(""); setPrayerVisibility("church"); setPosting(false);
    if (chip === "all") setPendingComposerType(null);
  }

  // ── Prayer ────────────────────────────────────────────────────────────
  const [expandedPrayer, setExpandedPrayer] = useState(null);
  const [prayerCommentIn, setPrayerCommentIn] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);

  // Deep link: a prayer-reply push points at a specific prayer. Auto-expand
  // its reply thread (not just highlight it) once the prayer has loaded.
  useEffect(() => {
    if (deepLinkHighlight?.type === "prayer" && deepLinkHighlight.id && prayers.some(p => p.id === deepLinkHighlight.id)) {
      setExpandedPrayer(deepLinkHighlight.id);
    }
  }, [deepLinkHighlight, prayers]);

  function togglePray(id) {
    const pr = prayers.find(p => p.id === id);
    if (pr) togglePrayRaw(id, pr.prayed);
  }
  function addPrayerComment(prayerId) {
    if (!prayerCommentIn.trim()) return;
    addPrayerCommentRaw(prayerId, prayerCommentIn.trim());
    setPrayerCommentIn("");
  }
  function likePrayerComment(prayerId, commentId) {
    const pr = prayers.find(p => p.id === prayerId);
    const c = pr?.comments.find(x => x.id === commentId);
    if (c) likePrayerCommentRaw(commentId, c.liked);
  }
  function handleDeletePrayer(pr) {
    if (!canDelete(pr.authorId)) return;
    const isOther = !!userId && pr.authorId !== userId;
    if (isOther) {
      setPendingDelete({ title: "This is a post from another church member, are you sure you want to delete?", subtitle: "All removed posts can be viewed in Removed Posts under profile.", onConfirm: () => { removePrayer(pr.id, pr.text); setPendingDelete(null); } });
    } else {
      if (!window.confirm("Delete this prayer request?")) return;
      removePrayer(pr.id, pr.text);
    }
  }

  // ── Encouragements ────────────────────────────────────────────────────
  const [encourVideoOnly, setEncourVideoOnly] = useState(false);
  const [encourSearch, setEncourSearch] = useState("");
  const [encourSettingsOpen, setEncourSettingsOpen] = useState(false);
  const [editingEncouragement, setEditingEncouragement] = useState(null);
  const [openEncId, setOpenEncId] = useState(null);
  const [encCommentDraft, setEncCommentDraft] = useState("");
  const [encReplyTo, setEncReplyTo] = useState(null);
  const [encReplyDraft, setEncReplyDraft] = useState("");
  const [commentError, setCommentError] = useState("");
  const openEnc = openEncId ? encouragements.find(e => e.id === openEncId) : null;

  function likeEncouragement(id) {
    const en = encouragements.find(e => e.id === id);
    if (en) toggleEncouragementLike(id, en.liked);
  }
  function handleDeleteEncouragement(en) {
    if (!canDelete(en.authorId)) return;
    const isOther = !!userId && en.authorId !== userId;
    if (isOther) {
      setPendingDelete({ title: "This is a post from another church member, are you sure you want to delete?", subtitle: "All removed posts can be viewed in Removed Posts under profile.", onConfirm: () => { removeEncouragement(en.id, en.text); setPendingDelete(null); } });
    } else {
      if (!window.confirm("Delete this encouragement?")) return;
      removeEncouragement(en.id, en.text);
    }
  }
  async function convertEncouragementToSermonNote(en) {
    if (!isModerator) return;
    const linkStr = String(en.link || "");
    const m = linkStr.match(/[?&]v=([A-Za-z0-9_-]{6,})|youtu\.be\/([A-Za-z0-9_-]{6,})|\/shorts\/([A-Za-z0-9_-]{6,})|\/embed\/([A-Za-z0-9_-]{6,})/);
    const ytId = m?.[1] || m?.[2] || m?.[3] || m?.[4] || null;
    if (!ytId) { window.alert("Cannot convert — no YouTube video link found on this encouragement."); return; }
    const { data: srm, error: sErr } = await supabase.from("sermons").select("id, title, church_id").eq("youtube_id", ytId).eq("church_id", en.church_id || activeChurchId).limit(1);
    if (sErr) { window.alert("Sermon lookup failed: " + sErr.message); return; }
    if (!srm?.length) { window.alert("Cannot convert — no matching sermon found in this church for that YouTube video."); return; }
    const sermon = srm[0];
    const { data: refRows, error: rErr } = await supabase.from("encouragement_refs").select("*").eq("encouragement_id", en.id);
    if (rErr) { window.alert("Could not read encouragement refs: " + rErr.message); return; }
    const refs = refRows || [];
    if (refs.length === 0) {
      if (!window.confirm("This encouragement has no Bible refs to copy. Convert anyway (delete it)?")) return;
    } else if (!window.confirm(`Convert this to ${refs.length} sermon ref${refs.length === 1 ? "" : "s"} under "${sermon.title || "the linked sermon"}"? The encouragement will be removed.`)) return;
    if (refs.length > 0) {
      const baseRows = dedupeRefRows(refs.map(r => ({
        sermon_id: sermon.id, church_id: sermon.church_id, verse_ref: r.verse_ref, book: r.book, chapter: r.chapter,
        verse: r.verse, end_verse: r.end_verse ?? r.verse ?? null, timestamp_s: r.timestamp_s ?? null, timestamp: r.timestamp || null, notes: r.notes || null,
      })));
      let { error: insErr } = await supabase.from("sermon_refs").insert(baseRows);
      if (insErr && /column.*does not exist/i.test(insErr.message || "")) {
        const msg = insErr.message || "";
        const keepEnd = !/end_verse/i.test(msg);
        const keepChurch = !/church_id/i.test(msg);
        const retry = baseRows.map(r => {
          // eslint-disable-next-line no-unused-vars
          const { end_verse, church_id, ...rest } = r;
          const row = keepEnd ? r : rest;
          if (!keepChurch) delete row.church_id;
          return row;
        });
        ({ error: insErr } = await supabase.from("sermon_refs").insert(retry));
      }
      if (insErr) { window.alert("Failed to create sermon refs: " + insErr.message); return; }
    }
    await removeEncouragement(en.id, en.text);
    refreshEncouragements?.();
  }
  async function submitEncComment(encId) {
    const text = (encCommentDraft || "").trim();
    if (!text) return;
    const { error } = await addEncComment(encId, text);
    if (error) { setCommentError(error); return; }
    setCommentError(""); setEncCommentDraft("");
  }
  async function submitEncReply(encId, parentId) {
    const text = (encReplyDraft || "").trim();
    if (!text) return;
    const { error } = await addEncComment(encId, text, parentId);
    if (error) { setCommentError(error); return; }
    setCommentError(""); setEncReplyDraft(""); setEncReplyTo(null);
  }

  // ── Board ─────────────────────────────────────────────────────────────
  const [commentDrafts, setCommentDrafts] = useState({});
  const [boardReplyTo, setBoardReplyTo] = useState(null);
  const [boardReplyDraft, setBoardReplyDraft] = useState("");
  const [openCommentsId, setOpenCommentsId] = useState(null);
  const openPost = openCommentsId ? posts.find(p => p.id === openCommentsId) : null;

  function handleDeletePost(p) {
    if (!canDelete(p.authorId)) return;
    const isOther = !!userId && p.authorId !== userId;
    if (isOther) {
      setPendingDelete({ title: "This is a post from another church member, are you sure you want to delete?", subtitle: "All removed posts can be viewed in Removed Posts under profile.", onConfirm: () => { removePost(p.id); setPendingDelete(null); } });
    } else {
      if (!window.confirm("Delete this post?")) return;
      removePost(p.id);
    }
  }
  async function handleBoardComment(postId) {
    const text = (commentDrafts[postId] || "").trim();
    if (!text) return;
    const { error } = await addBoardComment(postId, text);
    if (error) { setPostError(error); return; }
    setCommentDrafts(d => ({ ...d, [postId]: "" }));
    refreshBoard();
  }
  async function handleBoardReply(postId, parentId) {
    const text = (boardReplyDraft || "").trim();
    if (!text) return;
    const { error } = await addBoardComment(postId, text, parentId);
    if (error) { setPostError(error); return; }
    setBoardReplyDraft(""); setBoardReplyTo(null);
    refreshBoard();
  }

  // ── Merged "All" feed ─────────────────────────────────────────────────
  const merged = useMemo(() => {
    const p = prayers.map(x => ({ kind: "prayer", sortAt: x.createdAt || x.date, data: x }));
    const e = encouragements.map(x => ({ kind: "encourage", sortAt: x.createdAt || x.date, data: x }));
    const b = posts.map(x => ({ kind: "board", sortAt: x.createdAt || x.date, data: x }));
    return [...p, ...e, ...b].sort((a, b2) => new Date(b2.sortAt) - new Date(a.sortAt));
  }, [prayers, encouragements, posts]);

  const chips = [
    ["all", "All"],
    ["prayer", "Prayer"],
    ["encourage", "Encouragements"],
    ...(posts.length > 0 ? [["board", "Board"]] : []),
  ];

  function renderPrayerCard(pr, { compact } = {}) {
    const dlHi = deepLinkHighlight?.type === "prayer" && deepLinkHighlight.id === pr.id;
    const isAnswered = !!pr.answered_at;
    const onList = savedPrayerIds.has(pr.id);
    // The author's own latest comment since marking it answered — posted by
    // the "Tell [church]" flow in usePrayerList().tellChurch (useData.js),
    // reused here rather than a separate "update" data model.
    const latestUpdate = isAnswered
      ? [...pr.comments].reverse().find(c => c.authorId === pr.authorId)
      : null;
    return (
      <div key={pr.id} id={`dl-prayer-${pr.id}`} style={{ ...S.card, background: "var(--white)", ...(dlHi ? { boxShadow: "0 0 0 2px var(--gold)" } : null) }}>
        <div style={S.cardHeadRow}>
          <Avatar url={pr.authorAvatar} name={pr.author} size={34}/>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={S.cardAuthor}>{pr.author}</span>
            <span style={{ ...S.cardMetaLine, color: isAnswered ? SAGE_INK : "var(--stone)" }}>
              {fmtRelative(pr.createdAt)} · {isAnswered ? <span style={{ fontWeight: 700 }}>✓ Answered</span> : "🙏 Prayer request"}
            </span>
          </div>
          <div style={{ marginLeft: "auto" }}>
            <ContentActionsMenu canDelete={canDelete(pr.authorId)} isOwn={pr.authorId === userId} onDelete={() => handleDeletePrayer(pr)} onReport={() => onReport?.("prayer", pr.id)}/>
          </div>
        </div>
        {pr.visibility === "elders" && (
          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "#7A3B36", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Shield size={11} strokeWidth={2.2}/> Just elders
          </div>
        )}
        {(() => {
          const key = `prayer:${pr.id}`;
          const isLong = (pr.text || "").length > 240;
          const expanded = expandedIds.has(key);
          return (
            <>
              <div style={{ fontSize: 14.5, lineHeight: 1.5, color: "#3B2422", ...(isLong && !expanded ? S.clamp4 : null) }}>{pr.text}</div>
              {isLong && (
                <button type="button" onClick={() => toggleExpanded(key)} style={S.readMoreBtn}>{expanded ? "Show less" : "Read more"}</button>
              )}
            </>
          );
        })()}

        {isAnswered ? (
          latestUpdate && (
            <div style={{ background: "#E9F5EE", borderRadius: 12, padding: "10px 12px", fontSize: 13.5, color: "#2E5B44", lineHeight: 1.45 }}>
              "{latestUpdate.text}"
            </div>
          )
        ) : (
          <>
            {pr.prayCount > 0 && (
              <div style={S.cardHeadRow}>
                <FaceStack userIds={pr.prayingIds} profilesById={pr.prayingProfilesById} size={20}/>
                <span style={{ fontSize: 12, color: "var(--stone)" }}>
                  {pr.prayed ? `You and ${Math.max(0, pr.prayCount - 1)} other${pr.prayCount - 1 === 1 ? "" : "s"} are praying` : `${pr.prayCount} praying`}
                </span>
              </div>
            )}
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button type="button" onClick={() => togglePray(pr.id)}
                style={{ alignSelf: "flex-start", border: "none", background: pr.prayed ? "#F3D6D2" : "var(--cream)", color: pr.prayed ? "#8E2F22" : "var(--ink)", borderRadius: 999, padding: "8px 14px", fontFamily: SANS, fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>
                🙏 {pr.prayed ? "Praying" : "Pray"}
              </button>
              {!compact && (
                <button type="button" onClick={() => setExpandedPrayer(expandedPrayer === pr.id ? null : pr.id)}
                  style={{ alignSelf: "flex-start", background: "none", border: "none", color: "var(--stone)", fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS, padding: "8px 6px" }}>
                  💬 {pr.comments.length}
                </button>
              )}
              <button type="button" onClick={() => handleTogglePrayerList(pr.id, onList)}
                style={{ marginLeft: "auto", alignSelf: "flex-start", border: `1px solid ${onList ? SAGE_INK : "var(--border-strong)"}`, background: "transparent", color: onList ? SAGE_INK : "var(--stone)", borderRadius: 999, padding: "8px 14px", fontFamily: SANS, fontWeight: 700, fontSize: 12.5, cursor: "pointer" }}>
                {onList ? "✓ On my list" : "＋ My list"}
              </button>
            </div>
          </>
        )}

        {!compact && !isAnswered && expandedPrayer === pr.id && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {pr.comments.map(c => (
              <div key={c.id} style={{ background: "var(--cream)", borderRadius: 10, padding: 10 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Avatar url={c.authorAvatar} name={c.author} size={20}/>
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--ink)" }}>{c.author}</span>
                  <span style={{ fontSize: 11, color: "var(--stone)" }}>{c.date}</span>
                </div>
                <div style={{ fontSize: 13, color: "#3B2422", marginTop: 4 }}>{c.text}</div>
                <div onClick={() => likePrayerComment(pr.id, c.id)} style={{ cursor: "pointer", fontSize: 12, marginTop: 4 }}>
                  {c.liked ? "❤️" : "🤍"} {c.likes > 0 && c.likes}
                </div>
              </div>
            ))}
            <div style={{ display: "flex", gap: 8 }}>
              <textarea rows={2} placeholder="Write a supportive comment…" value={prayerCommentIn} onChange={e => setPrayerCommentIn(e.target.value)}
                style={{ flex: 1, background: "var(--cream)", border: "none", borderRadius: 10, padding: 8, fontSize: 13, fontFamily: SANS, outline: "none", resize: "none" }}/>
              <button onClick={() => addPrayerComment(pr.id)} disabled={!prayerCommentIn.trim()} style={{ background: "var(--cream)", border: "none", borderRadius: 10, padding: "0 14px", cursor: "pointer", color: "#7A3B36", fontWeight: 700 }}>↑</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  function renderEncouragementCard(en, { compact } = {}) {
    return (
      <div key={en.id} style={S.cardEnc}>
        <div style={S.cardHeadRow}>
          <Avatar url={en.authorAvatar} name={en.sharedBy || en.author} size={34}/>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={S.cardAuthor}>{en.sharedBy || en.author}</span>
            <span style={{ ...S.cardMetaLine, color: "#6B4526" }}>Encouragement · {fmtRelative(en.createdAt)}</span>
          </div>
          <div style={{ marginLeft: "auto" }}>
            <ContentActionsMenu
              canDelete={canDelete(en.authorId)}
              isOwn={en.authorId === userId}
              onDelete={() => handleDeleteEncouragement(en)}
              onReport={() => onReport?.("encouragement", en.id)}
              extraItems={isModerator ? [
                { label: "Edit", color: "var(--ink)", onClick: () => { setEditingEncouragement(en); setEncourSettingsOpen(true); } },
                { label: "Convert to sermon note", color: "var(--ink)", onClick: () => convertEncouragementToSermonNote(en) },
              ] : []}
            />
          </div>
        </div>
        {(en.name || en.title) && <div style={{ fontSize: 15, fontWeight: 700, color: "var(--ink)" }}>{en.name || en.title}</div>}
        {/* `text` can be the full verbatim sermon-transcript excerpt behind
            this encouragement (see buildEncouragementContent in
            AdminPanel.jsx) — too long/raw for a card. `summary` is the
            short AI one-liner meant for display; fall back to `text` only
            for older/manually-entered rows that never got a summary. */}
        {(() => {
          const body = en.summary || en.text || "";
          const key = `encourage:${en.id}`;
          const isLong = body.length > 240;
          const expanded = expandedIds.has(key);
          return (
            <>
              <div style={{ fontFamily: SERIF, fontSize: 16, lineHeight: 1.5, color: "#3D2614", ...(isLong && !expanded ? S.clamp4 : null) }}>“{body}”</div>
              {isLong && (
                <button type="button" onClick={() => toggleExpanded(key)} style={S.readMoreBtn}>{expanded ? "Show less" : "Read more"}</button>
              )}
            </>
          );
        })()}
        {en.refs && en.refs.length > 0 && (
          <div style={{ fontSize: 12, fontWeight: 700, color: "#6B4526" }}>
            {en.refs.map(r => (
              <span key={r.id} role="button" tabIndex={0} onClick={() => {
                const m = String(r.verseRef || "").match(/^(.+?)\s+(\d+)(?::(\d+))?/);
                if (m) onOpenVerse?.(m[1].trim(), parseInt(m[2], 10), m[3] ? parseInt(m[3], 10) : null);
              }} style={{ marginRight: 8, cursor: "pointer", textDecoration: "underline" }}>
                {r.verseRef}
              </span>
            ))}
          </div>
        )}
        {en.link && (
          <a href={en.link} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, fontWeight: 700, color: "#6B4526" }}>
            {en.link.includes("youtube") || en.link.includes("youtu.be") ? "▶ Watch the moment" : (en.linkLabel || en.link)}
          </a>
        )}
        {!compact && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
            <LikeButton liked={en.liked} count={en.likes} onClick={() => likeEncouragement(en.id)}/>
            <CommentButton count={en.commentCount || 0} label="Comment" onClick={() => { setOpenEncId(en.id); setEncCommentDraft(""); setEncReplyTo(null); }}/>
          </span>
        )}
      </div>
    );
  }

  function renderBoardCard(p, { compact } = {}) {
    const dlHi = deepLinkHighlight?.type === "post" && deepLinkHighlight.id === p.id;
    return (
      <div key={p.id} id={`dl-post-${p.id}`} style={{ ...S.card, background: "var(--white)", ...(dlHi ? { boxShadow: "0 0 0 2px var(--gold)" } : null) }}>
        <div style={S.cardHeadRow}>
          <Avatar url={p.authorAvatar} name={p.author} size={28}/>
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={S.cardAuthor}>{p.author}</span>
            <span style={{ ...S.cardMetaLine, color: SAGE_INK }}>Board · {fmtRelative(p.createdAt)}</span>
          </div>
          <div style={{ marginLeft: "auto" }}>
            <ContentActionsMenu canDelete={canDelete(p.authorId)} isOwn={p.authorId === userId} onDelete={() => handleDeletePost(p)} onReport={() => onReport?.("post", p.id)}/>
          </div>
        </div>
        {(() => {
          const key = `board:${p.id}`;
          const isLong = (p.text || "").length > 240;
          const expanded = expandedIds.has(key);
          return (
            <>
              <div style={{ fontSize: 14, color: "var(--ink)", whiteSpace: "pre-wrap", ...(isLong && !expanded ? S.clamp4 : null) }}>{p.text}</div>
              {isLong && (
                <button type="button" onClick={() => toggleExpanded(key)} style={S.readMoreBtn}>{expanded ? "Show less" : "Read more"}</button>
              )}
            </>
          );
        })()}
        {!compact && (
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <LikeButton liked={p.liked} count={p.likes} onClick={() => toggleBoardLike(p.id, p.liked)}/>
            <CommentButton count={p.comments.length} label="Comment" onClick={() => setOpenCommentsId(p.id)}/>
          </div>
        )}
      </div>
    );
  }

  function renderItem(item) {
    if (item.kind === "prayer") return renderPrayerCard(item.data, { compact: true });
    if (item.kind === "encourage") return renderEncouragementCard(item.data, { compact: true });
    return renderBoardCard(item.data, { compact: true });
  }

  const composerPlaceholder = effectiveType === "prayer"
    ? (prayerVisibility === "elders" ? "Share a private prayer request just with the elders…" : `What can ${churchName || "your church"} pray for?`)
    : effectiveType === "encourage"
      ? "Share an encouraging word…"
      : "Share something with the church…";

  const composingPrayer = effectiveType === "prayer";

  return (
    <div style={S.wrap}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div style={S.title}>Community</div>
        {onOpenFindChurches && (
          <button type="button" onClick={onOpenFindChurches}
            style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 6, background: "var(--white)", border: "1px solid var(--border-strong)", borderRadius: 999, padding: "8px 14px", fontSize: 12.5, fontWeight: 700, color: "var(--ink)", cursor: "pointer", fontFamily: SANS }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            Find churches
          </button>
        )}
      </div>

      {chips.length > 1 && (
        <ScopeToggle scope={chip} onChange={setChip} activeBg={SAGE} activeColor={SAGE_INK} options={chips}/>
      )}

      {(chip !== "encourage" || isModerator) ? (
        chip === "all" && !pendingComposerType ? (
          <button type="button" onClick={() => setComposerSheetOpen(true)} style={{ ...S.composerRow, width: "100%", cursor: "pointer" }}>
            <span style={{ flex: 1, textAlign: "left", color: "var(--stone)", fontFamily: SANS, fontSize: 14 }}>Share with {churchName || "your church"}…</span>
            <span style={S.postBtn}>Post</span>
          </button>
        ) : composingPrayer ? (
          <div style={S.composerRow}>
            <Avatar url={profile?.avatar_url} name={profile?.full_name} size={32}/>
            <input value={draft} onChange={e => setDraft(e.target.value)} placeholder={composerPlaceholder}
              onKeyDown={e => { if (e.key === "Enter") submitComposer(); }}
              style={S.composerInput}/>
            <button type="button" onClick={() => setPrayerAudienceOpen(o => !o)}
              style={{ flexShrink: 0, display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 999, cursor: "pointer", background: prayerVisibility === "elders" ? "rgba(232,200,122,0.18)" : "var(--cream)", border: "none", color: prayerVisibility === "elders" ? "var(--gold-ink)" : "var(--stone)", fontSize: 12, fontWeight: 700, fontFamily: SANS, whiteSpace: "nowrap" }}>
              {prayerVisibility === "elders" ? <Shield size={13}/> : "👥"} {prayerVisibility === "elders" ? "Elders" : "Church"} ▾
            </button>
            {draft.trim() && (
              <button type="button" onClick={submitComposer} disabled={posting} style={S.postBtn}>
                {posting ? "…" : "Post"}
              </button>
            )}
          </div>
        ) : (
          <div style={S.composerRow}>
            <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}>Share with {churchName || "your church"}</span>
            <input value={draft} onChange={e => setDraft(e.target.value)} placeholder={composerPlaceholder}
              onKeyDown={e => { if (e.key === "Enter") submitComposer(); }}
              style={S.composerInput}/>
            <button type="button" onClick={submitComposer} disabled={!draft.trim() || posting} style={{ ...S.postBtn, opacity: draft.trim() ? 1 : 0.5 }}>
              {posting ? "…" : "Post"}
            </button>
          </div>
        )
      ) : (
        <div style={{ fontSize: 12.5, color: "var(--stone)", fontStyle: "italic", padding: "0 4px" }}>
          To share your own word, please speak to a church leader.
        </div>
      )}
      {chip === "encourage" && isModerator && (
        <button onClick={() => setEncourSettingsOpen(true)} style={{ alignSelf: "flex-start", background: "none", border: "none", color: "var(--gold-ink)", fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: SANS, padding: 0 }}>
          + Add with a link, title or who it's from
        </button>
      )}

      {prayerAudienceOpen && composingPrayer && (
        <div style={{ display: "flex", gap: 8 }}>
          <div onClick={() => { setPrayerVisibility("church"); setPrayerAudienceOpen(false); }} style={{ cursor: "pointer", fontSize: 12, padding: "4px 8px" }}>👥 All members</div>
          <div onClick={() => { setPrayerVisibility("elders"); setPrayerAudienceOpen(false); }} style={{ cursor: "pointer", fontSize: 12, padding: "4px 8px" }}>🛡 Just elders</div>
        </div>
      )}
      {postError && <div style={{ fontSize: 12, color: "#C8372A" }}>{postError}</div>}

      {chip === "all" && notices.map(n => (
        <div key={n.id} style={S.noticeStrip}>
          {n.title && <span style={{ fontWeight: 800 }}>{n.title}: </span>}
          {n.message}
        </div>
      ))}

      {chip === "encourage" && encouragements.length > 0 && (() => {
        const hasAnyVideo = encouragements.some(en => /youtube|youtu\.be/.test(String(en.link || "")));
        return (
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            {hasAnyVideo && (
              <div style={{ display: "inline-flex", padding: 3, borderRadius: 999, background: "var(--white)", border: "1px solid var(--border)" }}>
                <button type="button" onClick={() => setEncourVideoOnly(false)}
                  style={{ background: !encourVideoOnly ? "var(--white)" : "transparent", border: "none", borderRadius: 999, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: !encourVideoOnly ? "var(--ink)" : "var(--stone)", cursor: "pointer", fontFamily: SANS }}>All</button>
                <button type="button" onClick={() => setEncourVideoOnly(true)}
                  style={{ background: encourVideoOnly ? SAGE : "transparent", border: "none", borderRadius: 999, padding: "6px 12px", fontSize: 12, fontWeight: 700, color: encourVideoOnly ? SAGE_INK : "var(--stone)", cursor: "pointer", fontFamily: SANS }}>Has video</button>
              </div>
            )}
            <input type="search" value={encourSearch} onChange={e => setEncourSearch(e.target.value)} placeholder="Search by who shared…"
              style={{ flex: 1, minWidth: 140, background: "var(--white)", border: "1px solid var(--border)", borderRadius: 10, padding: "8px 12px", fontSize: 13, fontFamily: SANS, outline: "none", color: "var(--ink)" }}/>
          </div>
        );
      })()}

      <div style={S.feed}>
        {chip === "all" && (merged.length === 0 ? <div style={S.empty}>Nothing shared yet — be the first.</div> : merged.map(renderItem))}
        {chip === "prayer" && (prayers.length === 0 ? <div style={S.empty}>No prayer requests yet.</div> : prayers.map(pr => renderPrayerCard(pr)))}
        {chip === "encourage" && (encouragements.length === 0 ? <div style={S.empty}>No encouragements yet.</div> : encouragements
          .filter(en => !encourVideoOnly || /youtube|youtu\.be/.test(String(en.link || "")))
          .filter(en => !encourSearch.trim() || String(en.sharedBy || en.author || "").toLowerCase().includes(encourSearch.trim().toLowerCase()))
          .map(en => renderEncouragementCard(en)))}
        {chip === "board" && (posts.length === 0 ? <div style={S.empty}>No posts yet.</div> : posts.map(p => renderBoardCard(p)))}
      </div>

      {openPost && (
        <>
          <div style={S.backdrop} onClick={() => setOpenCommentsId(null)}/>
          <div style={S.sheet}>
            <div style={S.grab}/>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 16px 0" }}>
              <div style={{ fontFamily: SERIF, fontSize: 17 }}>Comments</div>
              <button onClick={() => setOpenCommentsId(null)} style={S.closeBtn}>✕</button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "0 16px" }}>
              {openPost.comments.map(c => (
                <div key={c.id} style={{ padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}><Avatar url={c.authorAvatar} name={c.author} size={22}/><span style={{ fontWeight: 700, fontSize: 13 }}>{c.author}</span><span style={{ fontSize: 11, color: "var(--stone)" }}>{c.date}</span></div>
                  <div style={{ fontSize: 13, marginTop: 4 }}>{c.text}</div>
                  <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                    <LikeButton liked={c.liked} count={c.likes} onClick={() => likeBoardComment(c.id, c.liked)} size={14}/>
                    <button onClick={() => { setBoardReplyTo(boardReplyTo === c.id ? null : c.id); setBoardReplyDraft(""); }} style={{ background: "none", border: "none", color: "var(--stone)", fontSize: 12, cursor: "pointer" }}>Reply</button>
                  </div>
                  {boardReplyTo === c.id && (
                    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                      <input value={boardReplyDraft} onChange={e => setBoardReplyDraft(e.target.value)} onKeyDown={e => { if (e.key === "Enter") handleBoardReply(openPost.id, c.id); }} style={S.composerInput}/>
                      <button onClick={() => handleBoardReply(openPost.id, c.id)} style={S.postBtn}>Reply</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            <div style={{ padding: 12, display: "flex", gap: 8 }}>
              <input value={commentDrafts[openPost.id] || ""} onChange={e => setCommentDrafts(d => ({ ...d, [openPost.id]: e.target.value }))} onKeyDown={e => { if (e.key === "Enter") handleBoardComment(openPost.id); }} placeholder="Add a comment…" style={S.composerInput}/>
              <button onClick={() => handleBoardComment(openPost.id)} style={S.postBtn}>Send</button>
            </div>
          </div>
        </>
      )}

      {openEnc && (
        <>
          <div style={S.backdrop} onClick={() => setOpenEncId(null)}/>
          <div style={S.sheet}>
            <div style={S.grab}/>
            <div style={{ padding: "0 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}><Avatar url={openEnc.authorAvatar} name={openEnc.author} size={26}/><span style={{ fontWeight: 700 }}>{openEnc.author}</span><button onClick={() => setOpenEncId(null)} style={{ marginLeft: "auto", ...S.closeBtn }}>✕</button></div>
              <div style={{ fontSize: 14, marginTop: 8 }}>{openEnc.text}</div>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "0 16px" }}>
              {(openEnc.comments || []).map(c => (
                <div key={c.id} style={{ padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}><Avatar url={c.authorAvatar} name={c.author} size={22}/><span style={{ fontWeight: 700, fontSize: 13 }}>{c.author}</span><span style={{ fontSize: 11, color: "var(--stone)" }}>{c.date}</span></div>
                  <div style={{ fontSize: 13, marginTop: 4 }}>{c.text}</div>
                  <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                    <LikeButton liked={c.liked} count={c.likes} onClick={() => likeEncComment(c.id, c.liked)} size={14}/>
                    <button onClick={() => { setEncReplyTo(encReplyTo === c.id ? null : c.id); setEncReplyDraft(""); }} style={{ background: "none", border: "none", color: "var(--stone)", fontSize: 12, cursor: "pointer" }}>Reply</button>
                    {canDelete(c.authorId) && <button onClick={() => removeEncComment(c.id)} style={{ background: "none", border: "none", color: "#C8372A", fontSize: 12, cursor: "pointer" }}>Delete</button>}
                  </div>
                  {encReplyTo === c.id && (
                    <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
                      <input value={encReplyDraft} onChange={e => setEncReplyDraft(e.target.value)} onKeyDown={e => { if (e.key === "Enter") submitEncReply(openEnc.id, c.id); }} style={S.composerInput}/>
                      <button onClick={() => submitEncReply(openEnc.id, c.id)} style={S.postBtn}>Reply</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {commentError && <div style={{ padding: "0 16px", fontSize: 12, color: "#C8372A" }}>{commentError}</div>}
            <div style={{ padding: 12, display: "flex", gap: 8 }}>
              <input value={encCommentDraft} onChange={e => setEncCommentDraft(e.target.value)} onKeyDown={e => { if (e.key === "Enter") submitEncComment(openEnc.id); }} placeholder="Add a comment…" style={S.composerInput}/>
              <button onClick={() => submitEncComment(openEnc.id)} style={S.postBtn}>Send</button>
            </div>
          </div>
        </>
      )}

      {encourSettingsOpen && (
        <EncouragementSettingsSheet
          onClose={() => { setEncourSettingsOpen(false); setEditingEncouragement(null); }}
          churchId={activeChurchId}
          editing={editingEncouragement}
          onChanged={refreshEncouragements}
        />
      )}
      {pendingDelete && (
        <ConfirmDeleteSheet title={pendingDelete.title} subtitle={pendingDelete.subtitle} onCancel={() => setPendingDelete(null)} onConfirm={pendingDelete.onConfirm}/>
      )}
      {composerSheetOpen && (
        <ComposerTypeSheet
          isModerator={isModerator}
          onPick={(type) => { setPendingComposerType(type); setComposerSheetOpen(false); }}
          onCancel={() => setComposerSheetOpen(false)}
        />
      )}
      {prayerListToast && (
        <button type="button" onClick={() => { setPrayerListToast(false); onViewPrayerList?.(); }}
          style={{ position: "fixed", left: "50%", bottom: 90, transform: "translateX(-50%)", zIndex: 500, background: "var(--ink)", color: "#fff", fontSize: 12.5, fontWeight: 700, borderRadius: 999, padding: "10px 16px", whiteSpace: "nowrap", border: "none", cursor: "pointer", fontFamily: SANS }}>
          Added to your prayer list · View
        </button>
      )}
    </div>
  );
}

const S = {
  // Top is a plain 12px, not calc(env(safe-area-inset-top) + 12px) — .app
  // (the ancestor) already applies that env() once; re-adding it here would
  // double the gap on notched phones. Sides match the other 3 tabs (22px).
  wrap: { padding: "12px 22px calc(env(safe-area-inset-bottom) + 24px)", fontFamily: SANS, maxWidth: 560, margin: "0 auto", display: "flex", flexDirection: "column", gap: 12, overflowX: "hidden" },
  title: { fontFamily: SANS, fontWeight: 800, fontSize: 30, color: "var(--ink)", letterSpacing: "-0.02em" },
  empty: { fontSize: 13.5, color: "var(--stone)", padding: "30px 4px", textAlign: "center" },
  feed: { display: "flex", flexDirection: "column", gap: 12 },

  composerRow: { position: "relative", display: "flex", alignItems: "center", gap: 10, background: "var(--white)", borderRadius: 999, padding: "6px 6px 6px 16px" },
  composerInput: { flex: 1, border: "none", outline: "none", background: "none", fontFamily: SANS, fontSize: 14, color: "var(--ink)" },
  postBtn: { background: GOLD, color: "var(--ink)", border: "none", borderRadius: 999, padding: "10px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: SANS, whiteSpace: "nowrap" },

  noticeStrip: { borderRadius: 14, background: "var(--highlight)", padding: "10px 14px", fontSize: 13, color: "var(--ink)", lineHeight: 1.4 },

  card: { borderRadius: 24, padding: 16, display: "flex", flexDirection: "column", gap: 10, border: "1px solid var(--border)" },
  cardEnc: { borderRadius: 24, padding: 16, display: "flex", flexDirection: "column", gap: 10, background: "var(--peach)" },
  cardHeadRow: { display: "flex", alignItems: "center", gap: 10 },
  cardAuthor: { fontSize: 13.5, fontWeight: 700, color: "var(--ink)" },
  cardMetaLine: { fontSize: 11.5 },
  clamp4: { display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" },
  readMoreBtn: { alignSelf: "flex-start", background: "none", border: "none", padding: 0, fontSize: 12.5, fontWeight: 700, cursor: "pointer", fontFamily: SANS, color: "var(--gold-ink)" },

  backdrop: { position: "fixed", inset: 0, zIndex: 320, background: "rgba(0,0,0,0.5)" },
  sheet: { position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 321, background: "var(--cream)", borderTopLeftRadius: 20, borderTopRightRadius: 20, maxHeight: "80vh", display: "flex", flexDirection: "column", fontFamily: SANS },
  grab: { width: 38, height: 4, borderRadius: 2, background: "rgba(31,27,22,0.18)", margin: "10px auto" },
  closeBtn: { background: "rgba(31,27,22,0.06)", border: "none", color: "var(--stone)", width: 30, height: 30, borderRadius: 999, cursor: "pointer", fontSize: 14 },
};
